import { ChevronUp, ChevronDown, Trash2, Plus } from 'lucide-react';
import {
  MetadataForm,
  MetadataFormField,
  MetadataFormFieldType,
  newField,
} from '../types/metadataForm';

interface Props {
  value: MetadataForm;
  onChange: (v: MetadataForm) => void;
}

const TYPE_LABELS: Record<MetadataFormFieldType, string> = {
  text: 'Free text',
  number: 'Number',
  dropdown: 'Dropdown',
  'searchable-dropdown': 'Searchable dropdown',
};

const needsOptions = (t: MetadataFormFieldType) => t === 'dropdown' || t === 'searchable-dropdown';

export default function MetadataFormBuilder({ value, onChange }: Props) {
  const fields = value.fields ?? [];

  const update = (idx: number, patch: Partial<MetadataFormField>) => {
    const next = fields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    onChange({ ...value, fields: next });
  };

  const remove = (idx: number) => {
    onChange({ ...value, fields: fields.filter((_, i) => i !== idx) });
  };

  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange({ ...value, fields: next });
  };

  const add = () => onChange({ ...value, fields: [...fields, newField()] });

  // Marking a field as the "recording language" is mutually exclusive.
  const setLanguageRole = (idx: number, on: boolean) => {
    const next = fields.map((f, i) => {
      if (i === idx) return { ...f, role: on ? ('language' as const) : undefined };
      return on && f.role === 'language' ? { ...f, role: undefined } : f;
    });
    onChange({ ...value, fields: next });
  };

  return (
    <div className="space-y-3">
      {fields.map((f, idx) => (
        <div key={f.id} className="rounded-lg border border-gray-200 p-3 bg-gray-50/50">
          <div className="flex items-center gap-2 mb-2">
            <input
              className="input flex-1"
              placeholder="Field label (e.g. Occupation)"
              value={f.label}
              onChange={(e) => update(idx, { label: e.target.value })}
            />
            <div className="flex flex-col">
              <button
                type="button"
                className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                disabled={idx === 0}
                onClick={() => move(idx, -1)}
              >
                <ChevronUp size={16} />
              </button>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                disabled={idx === fields.length - 1}
                onClick={() => move(idx, 1)}
              >
                <ChevronDown size={16} />
              </button>
            </div>
            <button
              type="button"
              className="text-gray-400 hover:text-red-600 p-1"
              onClick={() => remove(idx)}
            >
              <Trash2 size={15} />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select
              className="input w-44"
              value={f.type}
              onChange={(e) => {
                const type = e.target.value as MetadataFormFieldType;
                update(idx, {
                  type,
                  // dropping to a non-language-capable type clears the role
                  role: type === 'searchable-dropdown' || type === 'dropdown' ? f.role : undefined,
                });
              }}
            >
              {(Object.keys(TYPE_LABELS) as MetadataFormFieldType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>

            <label className="flex items-center gap-1.5 text-sm text-gray-600">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                checked={!!f.required}
                onChange={(e) => update(idx, { required: e.target.checked })}
              />
              Required
            </label>

            {needsOptions(f.type) && (
              <label
                className="flex items-center gap-1.5 text-sm text-gray-600"
                title="Its answer becomes the session language fed to transcription"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                  checked={f.role === 'language'}
                  onChange={(e) => setLanguageRole(idx, e.target.checked)}
                />
                Recording language
              </label>
            )}
          </div>

          {needsOptions(f.type) && (
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Options (one per line)
              </label>
              <textarea
                className="input h-24 resize-y font-mono text-xs"
                placeholder={'Option 1\nOption 2\nOption 3'}
                value={(f.options ?? []).join('\n')}
                onChange={(e) =>
                  update(idx, {
                    options: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
                  })
                }
              />
              <div className="mt-1">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Default (optional)
                </label>
                <input
                  className="input"
                  placeholder="e.g. Hindi"
                  value={f.default ?? ''}
                  onChange={(e) => update(idx, { default: e.target.value || undefined })}
                />
              </div>
            </div>
          )}
        </div>
      ))}

      <button type="button" className="btn-secondary w-full" onClick={add}>
        <Plus size={15} /> Add Field
      </button>
    </div>
  );
}
